import { formatOtsDateTime } from "@ots-frontend/ots-data";
import { OtsTooltip } from "../components/OtsTooltip";

const DAY_MS = 86_400_000;

/**
 * How many calendar days `time` (read in `timeZone`) falls after the day being viewed: 1 for the next day,
 * -1 for the day before, 0 on the day itself or when there is no time.
 */
export const activityDayOffset = (time: number | undefined, day: Date, timeZone?: string): number => {
  if (time == null) return 0;
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "numeric", day: "numeric" }).formatToParts(time);
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((candidate) => candidate.type === type)?.value);
  const timeDay = Date.UTC(part("year"), part("month") - 1, part("day"));
  const viewedDay = Date.UTC(day.getFullYear(), day.getMonth(), day.getDate());
  return Math.round((timeDay - viewedDay) / DAY_MS);
};


export type ActivityDayOffsetProps = {
  time?: number;
  /** The day being viewed. */
  day?: Date;
  timeZone?: string;
};

/** "+1d" or "−1d" beside a time that falls outside the day being viewed, naming the day on hover. */
export const ActivityDayOffset = ({ time, day, timeZone }: ActivityDayOffsetProps) => {
  if (time == null || !day) return null;
  const offset = activityDayOffset(time, day, timeZone);
  if (offset === 0) return null;

  const days = Math.abs(offset);
  const when = offset > 0
    ? days === 1 ? "Next day" : `${days} days later`
    : days === 1 ? "Day before" : `${days} days earlier`;
  const label = `${when}, ${formatOtsDateTime(time, "weekday", { timeZone })}`;

  return (
    <OtsTooltip density="compact" content={label}>
      <span
        tabIndex={0}
        aria-label={label}
        data-day-offset={offset}
        className="shrink-0 rounded bg-[var(--ots-warning-container)] px-1 text-[10px] font-bold leading-4 text-[var(--ots-on-warning-container)] tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]">
        {offset > 0 ? "+" : "−"}{days}d
      </span>
    </OtsTooltip>
  );
};
